import React, { useEffect, useState } from "react";
import "./DisplayVisibility.css";
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from "react-redux";
import DisplayVisibilityForm from "./DisplayVisibilityForm";
import SideMenuComponent from "./DisplayVisibilityFormSideMenu";
import { getOutletDetails } from "../../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { popupType } from "../../../types/types";
import { GenericPopUp } from "../../popup/genericPopUp";
import { Loader } from "../../loader/Loader";
import { getDataFromLocalDb } from "../../../services/dbService";
import { DisplayVisibilityFormData } from "../../../features/claimsNew/utils/utils";
import MiniClaimSummary from "../../../features/claimsNew/components/MiniClaimSummary";
import { logLoadTrace } from "../../../../src/firebase";
import { setSelectedClaimSubType } from "../../../stateManagement/actions/claims";

const DisplayVisibility = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const locationState: any = location.state as any;
    const [isLoading, setLoader] = useState(false);
    const [allFinalClaims, setAllFinalClaims] = useState<DisplayVisibilityFormData[]>(locationState?.selectedRows || []);
    const [editData, setEditData] = useState<DisplayVisibilityFormData | null>(null);
    const [skuProductDescriptionMap, setSkuProductDescriptionMap] = useState<string[]>([]);
    const [outletDetails, setOutletDetails] = useState<any[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [genericModalState, setGenericModalState] = useState(false);
    const [genericModalMessage, setGenericModalSMessage] = useState("");
    const [genericModaltype, setGenericModalType] = useState<popupType>("Error");
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    const { currency } = ConfigDictionary;

    let claimSummary = [
        {
            label: "Claim ID",
            value: "0001234564564",
        },
        {
            label: "Claim Type",
            value: "Others",
        },
        {
            label: "Type of Expense",
            value: "Display/Visibility",
        },
        {
            label: "Selected Month",
            value: "Aug - 2024",
        },
        {
            label: "Final Claim Amount",
            value: "₹ 25,000.09",
        },
    ];

    const handleSave = (data: DisplayVisibilityFormData, proceed: boolean) => {
        let claimsAmount = 0;
        if (editData) {
            const updatedClaims = allFinalClaims.map(item =>
                item.id === editData.id ? data : item
            );
            updatedClaims.map((item) => {
                claimsAmount = claimsAmount + item.totalClaimAmount;
            });
            setAllFinalClaims(updatedClaims);
            setEditData(null);
            proceed && navigate(`summary?type=outletDetails`, { state: { "selectedRows": updatedClaims, "claimSummary": claimSummary } });
        } else {
            [...allFinalClaims, data].map((item) => {
                claimsAmount = claimsAmount + item.totalClaimAmount;
            });
            setAllFinalClaims([...allFinalClaims, data]);
            proceed && navigate(`summary?type=outletDetails`, { state: { "selectedRows": [...allFinalClaims, data], "claimSummary": claimSummary } });
        }
    };

    const openPopUp = (message: string, modalType: popupType) => {
        setGenericModalSMessage(message);
        setGenericModalState(true);
        setGenericModalType(modalType);
    };

    const handleEdit = (item: DisplayVisibilityFormData) => {
        if (hasUnsavedChanges) {
            openPopUp("Please save your current form before editing a different entry.", "Alert");
        } else {
            setEditData(item);
        }
    };

    const handleRemove = (id: string) => {
        setEditData((prev) => (prev && prev.id === id ? null : prev));
        setAllFinalClaims((prevClaims) => prevClaims.filter(item => item.id !== id));
    };

    const getInitialTableData = async () => {
        try {
            const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
            const responseOutletDetails = await getOutletDetails();
            const outletsList = responseOutletDetails.filter(
                (outlet: any) => outlet.outletCode !== loginId
            );
            setOutletDetails(outletsList);
            const productData = await getDataFromLocalDb("Product");
            const skuData = Object.values(productData?.data[0]?.data);

            const skuDescriptions: string[] = skuData.map(
                (item: any) => `${item.skuCode} - ${item.skuName ?? "N/A"}`
            );
            setSkuProductDescriptionMap(skuDescriptions);
        } catch (error) {
            console.error("Error while setting Initial Data for Form", error);
        }
    };

    const init = async () => {
        setLoader(true);
        await Promise.allSettled([
            getInitialTableData(),
        ]).finally(() => {
            setLoader(false);
        });
    };

    useEffect(() => {
        logLoadTrace(init, "claims_display_visibility_page_load_time");
    }, []);

    useEffect(() => {
        if (locationState?.selectedRows && locationState?.selectedIndex !== undefined) {
            const selectedClaim = locationState.selectedRows[locationState.selectedIndex];
            setEditData(selectedClaim);
        }
    }, [locationState]);

    useEffect(() => {
        return () => {
            if (!window.location.pathname.includes('displayVisibility')) {
                dispatch(setSelectedClaimSubType(""));
            }
        };
    }, []);

    return (
        <>
            <GenericPopUp
                type={genericModaltype}
                message={genericModalMessage}
                openGenericModal={genericModalState}
                setOpenGenericModal={() => {
                    setGenericModalState(false);
                }}
            />
            {
                isLoading ? <Loader sx={{ position: "absolute", top: "55%" }} /> :
                    <div className="display-visibility-container" >
                        {allFinalClaims.length > 0 ?
                            <div className="display-summary-section" >
                                <MiniClaimSummary rows={allFinalClaims} />
                            </div>
                            : <></>
                        }
                        <div className={`display-form-layout ${allFinalClaims.length === 0 ? 'no-claims' : 'with-claims'}`}>
                            {allFinalClaims.length > 0 && (
                                <div className="display-side-menu-container">
                                    <SideMenuComponent data={allFinalClaims} onEdit={handleEdit} onRemove={handleRemove} currencySymbol={currency}/>
                                </div>
                            )}
                            <div className="display-form-container">
                                <DisplayVisibilityForm
                                    key={editData?.id || "form"}
                                    onSave={handleSave}
                                    initialData={editData}
                                    skuProductDescriptionMap={skuProductDescriptionMap}
                                    customersInfo={outletDetails}
                                    currencySymbol={currency}
                                    setHasUnsavedChanges={setHasUnsavedChanges}
                                />
                            </div>
                        </div>
                    </div >
            }
        </>
    );
};

export default DisplayVisibility;
